// Fuzzy (typo-tolerant) matching for Thai/English search text.
// Mirror of backend/utils/fuzzy.js — keep the two in sync.

const ZERO_WIDTH_RE = /[​-‍﻿]/g;
// Tone mark typed before an upper vowel (e.g. ก + ่ + ี) → canonical order (ก + ี + ่)
const TONE_BEFORE_VOWEL_RE = /([่-๋])([ัิ-ื็])/g;

export function normalizeThai(str) {
  return String(str ?? '')
    .replace(ZERO_WIDTH_RE, '')
    .replace(/ํา/g, 'ำ') // นิคหิต + สระอา → สระอำ
    .replace(TONE_BEFORE_VOWEL_RE, '$2$1')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

// Allowed typos per token length. Tokens with digits (sizes like 2x2, 1.2) must match exactly.
export function maxDistanceFor(token) {
  if (/\d/.test(token)) return 0;
  const len = token.length;
  if (len < 3) return 0;
  if (len < 6) return 1;
  return 2;
}

// Smallest edit distance between `pattern` and any substring of `text` (Sellers' algorithm).
// Returns Infinity when the distance is above maxDist.
export function fuzzySubstringDistance(pattern, text, maxDist = Infinity) {
  const m = pattern.length;
  if (m === 0) return 0;
  let prev = new Array(m + 1);
  let cur = new Array(m + 1);
  for (let i = 0; i <= m; i++) prev[i] = i;
  let best = m;

  for (let j = 0; j < text.length; j++) {
    const c = text[j];
    cur[0] = 0;
    for (let i = 1; i <= m; i++) {
      const cost = pattern[i - 1] === c ? 0 : 1;
      cur[i] = Math.min(prev[i - 1] + cost, prev[i] + 1, cur[i - 1] + 1);
    }
    if (cur[m] < best) {
      best = cur[m];
      if (best === 0) return 0;
    }
    [prev, cur] = [cur, prev];
  }
  return best <= maxDist ? best : Infinity;
}

// Plain Levenshtein distance between two whole strings, Infinity when above maxDist.
export function levenshtein(a, b, maxDist = Infinity) {
  if (Math.abs(a.length - b.length) > maxDist) return Infinity;
  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  let cur = new Array(b.length + 1);
  for (let i = 1; i <= a.length; i++) {
    cur[0] = i;
    let rowMin = cur[0];
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      cur[j] = Math.min(prev[j - 1] + cost, prev[j] + 1, cur[j - 1] + 1);
      if (cur[j] < rowMin) rowMin = cur[j];
    }
    if (rowMin > maxDist) return Infinity;
    [prev, cur] = [cur, prev];
  }
  return prev[b.length] <= maxDist ? prev[b.length] : Infinity;
}

// 0 for exact substring, 1..2 for a tolerated typo, Infinity for no match.
export function tokenDistance(token, text) {
  if (text.includes(token)) return 0;
  const maxDist = maxDistanceFor(token);
  if (maxDist === 0) return Infinity;
  return fuzzySubstringDistance(token, text, maxDist);
}

export function tokenize(search) {
  return normalizeThai(search).split(' ').filter(Boolean);
}

// Total distance of all tokens against text, or Infinity if any token fails.
export function fuzzyScore(tokens, text) {
  let total = 0;
  for (const token of tokens) {
    const d = tokenDistance(token, text);
    if (d === Infinity) return Infinity;
    total += d;
  }
  return total;
}

const segmenter = typeof Intl !== 'undefined' && Intl.Segmenter
  ? new Intl.Segmenter('th', { granularity: 'word' })
  : null;

// Words (and runs of up to 3 adjacent words, since Thai has no spaces:
// "เหล็ก"+"กล่อง" → "เหล็กกล่อง") found in `texts`, mapped to how often they appear.
export function buildVocabulary(texts, maxRun = 3) {
  const vocab = new Map();
  for (const text of texts) {
    for (const chunk of normalizeThai(text).split(' ')) {
      if (!chunk) continue;
      const words = segmenter
        ? Array.from(segmenter.segment(chunk), s => s.segment)
        : [chunk];
      for (let i = 0; i < words.length; i++) {
        let run = '';
        for (let k = i; k < Math.min(i + maxRun, words.length); k++) {
          run += words[k];
          if (run.length >= 3) vocab.set(run, (vocab.get(run) || 0) + 1);
        }
      }
    }
  }
  return vocab;
}

// Closest vocabulary word to `token` (fewest edits, then most common), or null.
export function suggestWord(token, vocab) {
  const maxDist = maxDistanceFor(token);
  if (maxDist === 0) return null;
  let best = null;
  let bestDist = Infinity;
  let bestCount = 0;
  for (const [word, count] of vocab) {
    const d = levenshtein(token, word, Math.min(maxDist, bestDist));
    if (d < bestDist || (d === bestDist && d !== Infinity && count > bestCount)) {
      best = word;
      bestDist = d;
      bestCount = count;
    }
  }
  return bestDist === Infinity ? null : best;
}

// Rewrites the search so every token that appears in none of `texts` is swapped for its
// closest vocabulary word. Returns null when there is nothing to correct.
export function suggestCorrection(search, texts, vocab) {
  const tokens = tokenize(search);
  let changed = false;
  const corrected = tokens.map(token => {
    if (texts.some(t => t.includes(token))) return token;
    const word = suggestWord(token, vocab);
    if (!word) return token;
    changed = true;
    return word;
  });
  return changed ? corrected.join(' ') : null;
}
